import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLanguage, useTranslation } from "@/components/LanguageContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_wp-landing-builder/artifacts/p3s982cv_1.jpg";
const LOGO_FALLBACK = "https://customer-assets.emergentagent.com/job_wp-landing-builder/artifacts/p3s982cv_1.jpg";

const certLogos = [
  { src: "https://customer-assets.emergentagent.com/job_wp-landing-builder/artifacts/0a0pgq4o_EASA_Logo.png", alt: "EASA" },
];

const FLAG_IMAGES = {
  pt: "https://flagcdn.com/w80/pt.png",
  es: "https://flagcdn.com/w80/es.png",
  en: "https://flagcdn.com/w80/gb.png",
  da: "https://flagcdn.com/w80/dk.png",
  fo: "https://flagcdn.com/w80/fo.png",
};

const navLinks = [
  { key: "about", href: "/#sobre" },
  { key: "app", href: "/app" },
  { key: "courses", href: "/cursos", glow: true },
  { key: "exams", href: "/#exames", highlight: true },
  { key: "partnerships", href: "/#parcerias" },
  { key: "calendar", href: "/#calendario" },
  { key: "contact", href: "/#contacto" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, setLang, LANGUAGES } = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Smart navigation: SPA route changes use React Router; same-page hash links keep native scroll behaviour
  const handleNav = (e, href) => {
    if (href.startsWith("/#")) {
      const hash = href.slice(1); // "#sobre"
      if (location.pathname !== "/") {
        e.preventDefault();
        navigate("/" + hash);
        return;
      }
      // same page: let native anchor jump handle it
      return;
    }
    if (href.startsWith("/") && !href.startsWith("//")) {
      e.preventDefault();
      navigate(href);
    }
  };

  return (
    <>
      {/* Language Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-[#020518] h-9 flex items-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full flex items-center justify-between">
          <p className="text-[10px] text-slate-500 font-medium hidden sm:block">EASA Part 66 Aviation Academy — Portugal</p>
          <div className="flex items-center gap-1.5 ml-auto">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className={`rounded-sm overflow-hidden transition-all duration-200 ${
                  lang === l.code
                    ? "ring-2 ring-[#F59E0B] ring-offset-1 ring-offset-[#020518] scale-110 opacity-100"
                    : "opacity-40 hover:opacity-80 hover:scale-105"
                }`}
                title={l.label}
                data-testid={`lang-${l.code}`}
              >
                <img
                  src={FLAG_IMAGES[l.code]}
                  alt={l.label}
                  className="w-7 h-5 object-cover"
                  loading="eager"
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        data-testid="header"
        className={`fixed top-9 left-0 right-0 z-50 glass-header border-b transition-all duration-300 ${
          scrolled ? "border-slate-200/80 shadow-sm" : "border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-[76px]">
          <div className="flex items-center gap-4">
            <a href="/" onClick={(e) => handleNav(e, "/")} className="flex items-center gap-3" data-testid="header-logo">
              <div className="h-14 w-14 rounded-xl overflow-hidden bg-[#0A1628] shadow-md ring-1 ring-[#0A1628]/40 flex items-center justify-center shrink-0">
                <img
                  src={LOGO_FALLBACK}
                  alt="L4C Aviation Academy"
                  className="h-full w-full object-cover"
                  style={{ imageRendering: "auto" }}
                  loading="eager"
                />
              </div>
              <div className="hidden sm:block leading-tight">
                <span className="font-['Outfit'] font-bold text-[15px] text-[#0A1628] block">
                  L4C Aviation Academy
                </span>
                <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-[#00A8FF]">
                  EASA Part 66
                </span>
              </div>
            </a>
            <div className="hidden 2xl:flex items-center border-l border-slate-200 pl-4 ml-1">
              {certLogos.map((logo, i) => (
                <img
                  key={i}
                  src={logo.src}
                  alt={logo.alt}
                  className="h-8 w-auto object-contain"
                  style={{ mixBlendMode: "multiply" }}
                  title={logo.alt}
                  data-testid={`cert-logo-${i}`}
                />
              ))}
            </div>
          </div>

          <nav className="hidden xl:flex items-center gap-5">
            {navLinks.map((link) => {
              const label = t(`nav.${link.key}`);
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNav(e, link.href)}
                  className={`text-[12.5px] font-semibold transition-colors duration-200 uppercase tracking-wide whitespace-nowrap ${
                    link.highlight
                      ? "text-white bg-[#F59E0B] px-3 py-1.5 rounded-md shadow-md shadow-amber-500/25 hover:bg-[#D97706]"
                      : link.glow
                      ? "text-[#00A8FF] animate-pulse-glow relative"
                      : "text-slate-500 hover:text-[#0A1628]"
                  }`}
                  data-testid={`nav-${link.key}`}
                >
                  {label}
                </a>
              );
            })}
          </nav>

          <div className="hidden xl:block">
            <a
              href="/#contacto"
              onClick={(e) => handleNav(e, "/#contacto")}
              className="btn-primary-gold py-2 px-5 text-[13px] rounded-md inline-block whitespace-nowrap"
              data-testid="header-cta"
            >
              {t("nav.start")}
            </a>
          </div>

          <button
            className="xl:hidden p-2 text-slate-700"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="xl:hidden bg-white border-t border-slate-100 px-6 py-4 animate-fade-in shadow-lg" data-testid="mobile-menu">
            {/* Mobile flags */}
            <div className="flex items-center gap-2 pb-3 mb-3 border-b border-slate-100">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`rounded-sm overflow-hidden transition-all ${
                    lang === l.code ? "ring-2 ring-[#F59E0B] scale-110" : "opacity-40"
                  }`}
                  title={l.label}
                >
                  <img src={FLAG_IMAGES[l.code]} alt={l.label} className="w-8 h-5.5 object-cover" />
                </button>
              ))}
            </div>
            {navLinks.map((link) => {
              const label = t(`nav.${link.key}`);
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => { handleNav(e, link.href); setMobileOpen(false); }}
                  className={`block py-3 text-sm font-semibold border-b border-slate-50 ${
                    link.highlight
                      ? "text-white bg-[#F59E0B] font-bold px-3 -mx-3 rounded-md"
                      : link.glow
                      ? "text-[#00A8FF] font-bold animate-pulse-glow"
                      : "text-slate-600 hover:text-[#0A1628]"
                  }`}
                >
                  {label}
                </a>
              );
            })}
            <a
              href="/#contacto"
              onClick={(e) => { handleNav(e, "/#contacto"); setMobileOpen(false); }}
              className="mt-4 block text-center btn-primary-gold py-3 text-sm rounded-md"
            >
              {t("nav.start")}
            </a>
          </div>
        )}
      </header>
    </>
  );
}
